import {
  ActionIcon,
  Anchor,
  Button,
  Group,
  Loader,
  Stack,
  Text,
  Tooltip,
  UnstyledButton,
} from '@mantine/core';
import { ReactNode, useEffect, useRef, useState } from 'react';
import {
  IconChevronDown,
  IconChevronRight,
  IconChevronUp,
  IconCircleCheck,
  IconCheck,
  IconExclamationCircle,
  IconNote,
  IconTrash,
} from '@tabler/icons-react';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { DetectedClient, UploadedFile } from '../../hooks/file-uploads/types';
import DocumentActionsPopover from './DocumentActionsPopover';
import RenameDocumentControl from './RenameDocumentControl';

export type FiledInfo = {
  folderName: string;
  count: number;
  folderId: string | null;
};

const OWNER_PROCESS_KEY = '__owner__';

type AddDocumentRowProps = {
  file: UploadedFile;
  filedInfo: FiledInfo | undefined;
  rootFolderLabel: string;
  contactName: string | null;
  onDownload: (file: UploadedFile) => void;
  onDelete: (file: UploadedFile) => void;
  onUpdateDate: (file: UploadedFile, date: Date) => void;
  onProcess: (file: UploadedFile, clientId?: string) => void;
  onRetry?: (file: UploadedFile) => void;
  isRetrying?: boolean;
  onFileTo: (file: UploadedFile) => void;
  onOpenFiledFolder: (info: FiledInfo) => void;
  onSetResolved: (file: UploadedFile, isResolved: boolean) => void;
  onUploadToSharepoint?: (file: UploadedFile) => void;
  onDismissDetectedClient?: (file: UploadedFile, clientId: string) => void;
  dismissingClientId?: string | null;
  defaultExpanded?: boolean;
};

export default function AddDocumentRow({
  file,
  filedInfo,
  rootFolderLabel,
  contactName,
  onDownload,
  onDelete,
  onUpdateDate,
  onProcess,
  onRetry,
  isRetrying,
  onFileTo,
  onOpenFiledFolder,
  onSetResolved,
  onUploadToSharepoint,
  onDismissDetectedClient,
  dismissingClientId,
  defaultExpanded,
}: AddDocumentRowProps) {
  const [expanded, setExpanded] = useState(defaultExpanded ?? false);
  const [processingKey, setProcessingKey] = useState<string | null>(null);
  const [resolvingMemberId, setResolvingMemberId] = useState<string | null>(null);

  const status = file.backgroundProcess?.status;
  const isPending = file.id.startsWith('pending-');
  const isExtracting = status === 'PENDING' || status === 'PROCESSING';
  const isFailed = status === 'FAILED';
  const isReady = status === 'SUCCESS' && !file.isResolved;
  const isResolvedUnfiled = status === 'SUCCESS' && file.isResolved && !file.file.isFiled;
  const isFiled = !!filedInfo;
  const members = file.detectedClients;
  const hasNothingToProcess = members.length === 0 && !contactName;
  const hasPanel = isReady || isResolvedUnfiled || isFiled;
  const expandedView = hasPanel && expanded;
  const isCard = expandedView || isFailed;

  const wasExtractingRef = useRef(isExtracting);
  useEffect(() => {
    if (wasExtractingRef.current && isReady) setExpanded(true);
    wasExtractingRef.current = isExtracting;
  }, [isExtracting, isReady]);

  const menu = isPending ? null : (
    <DocumentActionsPopover
      file={file}
      onDownload={onDownload}
      onDelete={onDelete}
      onUpdateDate={onUpdateDate}
      onSetResolved={onSetResolved}
      onUploadToSharepoint={onUploadToSharepoint}
    />
  );

  const renameFileId = isPending ? undefined : file.file.id;

  const handleProcess = (memberId?: string) => {
    setProcessingKey(memberId ?? OWNER_PROCESS_KEY);
    onProcess(file, memberId);
  };

  const handleDismiss = (memberId: string) => {
    if (members.length === 1) {
      setResolvingMemberId(memberId);
      onSetResolved(file, true);
      return;
    }
    onDismissDetectedClient?.(file, memberId);
  };

  const header = getHeader();
  const readyRows = getReadyRows();

  return (
    <Stack
      gap="xs"
      w="100%"
      p={isCard ? 'sm' : undefined}
      bg={isCard ? 'white' : undefined}
      style={
        isCard ? { border: '1px solid var(--mantine-color-gray-1)', borderRadius: 12 } : undefined
      }
      data-testid="document-card"
    >
      {header}
      {isFailed && (
        <FailedRow onRetry={onRetry ? () => onRetry(file) : undefined} isRetrying={isRetrying} />
      )}
      {expandedView && (
        <Stack gap="xxs" w="100%">
          {isReady && readyRows}
          {!isReady && isFiled && filedInfo && (
            <FiledRow
              folderName={filedInfo.folderName}
              count={filedInfo.count}
              onOpen={() => onOpenFiledFolder(filedInfo)}
            />
          )}
          {!isReady && !isFiled && isResolvedUnfiled && (
            <FileToRow
              rootFolderLabel={rootFolderLabel}
              onFileTo={() => onFileTo(file)}
              processed={members.length > 0 || !!contactName}
            />
          )}
        </Stack>
      )}
    </Stack>
  );

  function getHeader() {
    if (isExtracting) {
      return (
        <RowHeader
          fileName={file.file.fileName}
          bg="white"
          extractingBorder
          testId="status-bar-extracting"
          summary={<ExtractingSummary />}
          menu={menu}
          renameFileId={renameFileId}
        />
      );
    }

    if (isFailed) {
      return (
        <RowHeader
          fileName={file.file.fileName}
          bg="transparent"
          flat
          menu={menu}
          renameFileId={renameFileId}
        />
      );
    }

    return (
      <RowHeader
        fileName={file.file.fileName}
        bg={expandedView ? 'transparent' : 'gray.0'}
        expandable={hasPanel}
        expanded={expanded}
        onToggle={() => setExpanded(prev => !prev)}
        summary={
          hasPanel ? <ChevronSummary cta={getCollapsedCta()} expanded={expanded} /> : undefined
        }
        menu={menu}
        flat={expandedView}
        renameFileId={renameFileId}
      />
    );
  }

  function getCollapsedCta() {
    if (isReady && hasNothingToProcess) return { label: 'File document', color: 'brand.7' };
    if (isReady) return { label: 'Process data', color: 'brand.7' };
    if (isFiled) return { label: 'Filed', color: 'gray.5' };
    return { label: 'Processed', color: 'gray.5' };
  }

  function getReadyRows() {
    if (members.length > 0) {
      return members.map(detectedClient => {
        if (detectedClient.isProcessed) {
          return <ProcessedMemberRow key={detectedClient.id} name={fullName(detectedClient)} />;
        }
        const isDismissing =
          members.length === 1
            ? resolvingMemberId === detectedClient.id
            : dismissingClientId === detectedClient.id;
        return (
          <PendingMemberRow
            key={detectedClient.id}
            name={fullName(detectedClient)}
            onProcess={() => handleProcess(detectedClient.id)}
            onDismiss={() => handleDismiss(detectedClient.id)}
            isProcessing={processingKey === detectedClient.id}
            isDismissing={isDismissing}
          />
        );
      });
    }

    if (contactName) {
      return (
        <OwnerReadyRow
          name={contactName}
          onProcess={() => handleProcess()}
          isProcessing={processingKey === OWNER_PROCESS_KEY}
        />
      );
    }

    return (
      <FileToRow
        rootFolderLabel={rootFolderLabel}
        onFileTo={() => onFileTo(file)}
        processed={false}
      />
    );
  }
}

function RowHeader({
  fileName,
  bg,
  extractingBorder,
  testId,
  expandable,
  expanded,
  onToggle,
  summary,
  menu,
  flat,
  renameFileId,
}: {
  fileName: string;
  bg: string;
  extractingBorder?: boolean;
  testId?: string;
  expandable?: boolean;
  expanded?: boolean;
  onToggle?: () => void;
  summary?: ReactNode;
  menu?: ReactNode;
  flat?: boolean;
  renameFileId?: number;
}) {
  const nameGroup = (
    <Group gap="xs" wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
      <IconNote size={24} color="var(--mantine-color-gray-3)" style={{ flexShrink: 0 }} />
      <Text size="sm" fw={FONT_WEIGHTS.medium} c="black" truncate style={{ flex: 1, minWidth: 0 }}>
        {fileName}
      </Text>
    </Group>
  );

  return (
    <Group
      bg={bg}
      px={flat ? 0 : 'sm'}
      py={flat ? 0 : 'sm'}
      gap="xs"
      w="100%"
      wrap="nowrap"
      style={{
        borderRadius: 12,
        border: extractingBorder ? '1px solid var(--mantine-color-brand-6)' : undefined,
      }}
      data-testid={testId}
    >
      {expandable ? (
        <UnstyledButton
          onClick={onToggle}
          aria-expanded={expanded}
          aria-label={expanded ? 'Collapse document' : 'Expand document'}
          style={{ flex: 1, minWidth: 0 }}
        >
          {nameGroup}
        </UnstyledButton>
      ) : (
        nameGroup
      )}
      {renameFileId !== undefined && (
        <RenameDocumentControl fileId={renameFileId} currentName={fileName} />
      )}
      {expandable && summary ? (
        <Group
          gap={0}
          wrap="nowrap"
          onClick={onToggle}
          style={{ flexShrink: 0, cursor: 'pointer' }}
        >
          {summary}
        </Group>
      ) : (
        summary
      )}
      {menu}
    </Group>
  );
}

function ExtractingSummary() {
  return (
    <Group gap="xs" wrap="nowrap" style={{ flexShrink: 0 }}>
      <Text size="sm" fw={FONT_WEIGHTS.medium} c="gray.6">
        Extracting data
      </Text>
      <Loader size={16} color="brand.6" />
    </Group>
  );
}

function FailedRow({ onRetry, isRetrying }: { onRetry?: () => void; isRetrying?: boolean }) {
  return (
    <Group
      bg="error.1"
      px="sm"
      py="xs"
      gap="xxs"
      w="100%"
      wrap="nowrap"
      style={{ borderRadius: 20 }}
      data-testid="status-bar-error"
    >
      <IconExclamationCircle
        size={24}
        color="var(--mantine-color-error-6)"
        style={{ flexShrink: 0 }}
      />
      <Text size="sm" fw={FONT_WEIGHTS.medium} c="gray.10" style={{ flex: 1 }}>
        Something went wrong, please{' '}
        {onRetry ? (
          <RetryLink onRetry={onRetry} isRetrying={isRetrying} />
        ) : (
          <PlaceholderRetryLink />
        )}
      </Text>
    </Group>
  );
}

function ChevronSummary({
  cta,
  expanded,
}: {
  cta: { label: string; color: string };
  expanded: boolean;
}) {
  const chevronColor = `var(--mantine-color-${cta.color.replace('.', '-')})`;

  return (
    <Group gap="xxs" wrap="nowrap" style={{ flexShrink: 0 }}>
      <Text size="xs" fw={FONT_WEIGHTS.semibold} c={cta.color}>
        {cta.label}
      </Text>
      {expanded ? (
        <IconChevronUp size={16} color={chevronColor} />
      ) : (
        <IconChevronDown size={16} color={chevronColor} />
      )}
    </Group>
  );
}

function PendingMemberRow({
  name,
  onProcess,
  onDismiss,
  isProcessing,
  isDismissing,
}: {
  name: string;
  onProcess: () => void;
  onDismiss: () => void;
  isProcessing?: boolean;
  isDismissing?: boolean;
}) {
  return (
    <Group
      bg="brand.0"
      px="sm"
      py="xs"
      gap="xxs"
      w="100%"
      wrap="nowrap"
      style={{ borderRadius: 20 }}
      data-testid="status-bar-ready"
    >
      <Text size="sm" fw={FONT_WEIGHTS.medium} c="black" style={{ flex: 1 }}>
        {name}
      </Text>
      <Group gap="xxs" wrap="nowrap">
        <ActionIcon
          variant="subtle"
          color="brand.7"
          size={24}
          radius={8}
          aria-label={`Remove ${name}`}
          data-testid="dismiss-contact-button"
          loading={isDismissing}
          disabled={isDismissing}
          onClick={onDismiss}
        >
          <IconTrash size={16} />
        </ActionIcon>
        <Button
          color="brand.6"
          c="white"
          h={24}
          px="xxs"
          radius={8}
          fz="xs"
          fw={FONT_WEIGHTS.semibold}
          loading={isProcessing}
          loaderProps={{ size: 14, color: 'white' }}
          onClick={onProcess}
          data-testid="process-data-button"
        >
          Process
        </Button>
      </Group>
    </Group>
  );
}

function OwnerReadyRow({
  name,
  onProcess,
  isProcessing,
}: {
  name: string;
  onProcess: () => void;
  isProcessing?: boolean;
}) {
  return (
    <Group
      bg="brand.0"
      px="sm"
      py="xs"
      gap="xxs"
      w="100%"
      wrap="nowrap"
      style={{ borderRadius: 20 }}
      data-testid="status-bar-ready"
    >
      <Text size="sm" fw={FONT_WEIGHTS.medium} c="black" style={{ flex: 1 }}>
        {name}
      </Text>
      <Button
        color="brand.6"
        c="white"
        h={24}
        px="xxs"
        radius={8}
        fz="xs"
        fw={FONT_WEIGHTS.semibold}
        loading={isProcessing}
        loaderProps={{ size: 14, color: 'white' }}
        onClick={onProcess}
        data-testid="process-data-button"
      >
        Process
      </Button>
    </Group>
  );
}

function ProcessedMemberRow({ name }: { name: string }) {
  return (
    <Group
      bg="gray.0"
      px="sm"
      py="xs"
      gap="xxs"
      w="100%"
      wrap="nowrap"
      style={{ borderRadius: 20 }}
      data-testid="status-bar-done"
    >
      <IconCheck size={20} color="var(--mantine-color-gray-6)" style={{ flexShrink: 0 }} />
      <Text size="sm" fw={FONT_WEIGHTS.medium} c="gray.6" style={{ flex: 1 }}>
        {name}
      </Text>
    </Group>
  );
}

function FileToRow({
  rootFolderLabel,
  onFileTo,
  processed,
}: {
  rootFolderLabel: string;
  onFileTo: () => void;
  processed: boolean;
}) {
  return (
    <Group
      bg="gray.0"
      px="sm"
      py="xs"
      gap="xxs"
      w="100%"
      wrap="nowrap"
      style={{ borderRadius: 20 }}
      data-testid="status-bar-file-to"
    >
      <IconCircleCheck size={20} color="var(--mantine-color-gray-6)" style={{ flexShrink: 0 }} />
      <Text size="sm" fw={FONT_WEIGHTS.medium} c="gray.10" style={{ flex: 1 }}>
        {processed && 'All processed. '}
        <Anchor
          component="button"
          type="button"
          underline="never"
          size="sm"
          fw={FONT_WEIGHTS.medium}
          c="brand.7"
          onClick={onFileTo}
          aria-label={`File to ${rootFolderLabel}`}
          data-testid="file-to-link"
        >
          File this document
        </Anchor>{' '}
        to keep data organised.
      </Text>
    </Group>
  );
}

function FiledRow({
  folderName,
  count,
  onOpen,
}: {
  folderName: string;
  count: number;
  onOpen: () => void;
}) {
  return (
    <UnstyledButton onClick={onOpen} w="100%" data-testid="status-bar-filed">
      <Group
        bg="gray.0"
        px="sm"
        py="xs"
        gap="xxs"
        w="100%"
        wrap="nowrap"
        style={{ borderRadius: 20 }}
      >
        <IconCircleCheck size={20} color="var(--mantine-color-gray-6)" style={{ flexShrink: 0 }} />
        <Text size="sm" fw={FONT_WEIGHTS.medium} c="gray.10" style={{ flex: 1 }}>
          Filed to &lsquo;{folderName}&rsquo; ({count})
        </Text>
        <IconChevronRight size={16} color="var(--mantine-color-gray-6)" style={{ flexShrink: 0 }} />
      </Group>
    </UnstyledButton>
  );
}

function RetryLink({ onRetry, isRetrying }: { onRetry: () => void; isRetrying?: boolean }) {
  return (
    <Anchor
      component="button"
      type="button"
      underline="never"
      size="sm"
      fw={FONT_WEIGHTS.medium}
      c="brand.6"
      disabled={isRetrying}
      onClick={onRetry}
      data-testid="status-bar-error-retry"
    >
      {isRetrying ? 'processing…' : 'try to process again'}
    </Anchor>
  );
}

function PlaceholderRetryLink() {
  return (
    <Tooltip label="Not implemented yet" withArrow>
      <Anchor
        component="button"
        type="button"
        underline="never"
        size="sm"
        fw={FONT_WEIGHTS.medium}
        c="brand.6"
        data-testid="status-bar-error-retry"
      >
        try to process again
      </Anchor>
    </Tooltip>
  );
}

const fullName = (member: DetectedClient) => `${member.firstName} ${member.lastName}`;
